import React, { useState, useEffect, useRef } from 'react';
import { Send, User as UserIcon } from 'lucide-react';

interface Message {
    userId: string;
    username: string;
    message: string;
    timestamp: number;
}

interface ChatPanelProps {
    gameId: string;
    socket: any;
    currentUser: any;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ gameId, socket, currentUser }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = (msg: Message) => {
            setMessages(prev => [...prev, msg]);
        };

        socket.on('new_chat_message', handleNewMessage);
        return () => {
            socket.off('new_chat_message', handleNewMessage);
        };
    }, [socket]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = () => {
        if (!inputValue.trim() || !socket) return;
        socket.emit('chat_message', { gameId, message: inputValue });
        setInputValue('');
    };

    return (
        <div className="flex flex-col h-full bg-[#1a1c24]/40 lg:rounded-2xl border border-white/5 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5 bg-white/[0.01]">
                <Send size={14} className="text-indigo-400" />
                <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em]">Chat de Partida</h4>
            </div>

            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 custom-scrollbar"
            >
                {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center opacity-10">
                        <span className="text-[9px] font-black uppercase tracking-widest">Sin mensajes</span>
                    </div>
                ) : (
                    messages.map((msg, i) => {
                        const isMe = msg.userId === currentUser?.id;
                        return (
                            <div key={i} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                <div className="flex items-center gap-1.5 mb-1 px-1">
                                    <span className="text-[8px] font-black text-white/20 uppercase tracking-tighter">
                                        {msg.username}
                                    </span>
                                </div>
                                <div className={`
                                    px-3 py-2 rounded-2xl text-[11px] max-w-[85%] break-words
                                    ${isMe ? 'bg-indigo-500 text-white rounded-tr-sm' : 'bg-white/5 text-white/80 rounded-tl-sm border border-white/5'}
                                `}>
                                    {msg.message}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="p-3 bg-white/[0.01] border-t border-white/5">
                <div className="relative flex items-center">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder="Escribe un mensaje..."
                        className="w-full bg-[#0a0c10]/50 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-indigo-500/50 transition-all"
                    />
                    <button
                        onClick={handleSend}
                        className="absolute right-2 p-1.5 text-indigo-400 hover:text-indigo-300 transition-colors"
                    >
                        <Send size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatPanel;
